import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "components/widgets/navbar/Transparent-nav";
import styled from "styled-components";
import Features from "components/landing/Features";
import video from "../../assets/videos/insurance.mp4"
import Services from "components/landing/ServicesSection";
import GrokIcon from "components/widgets/GrokIcon";
import { useRef } from "react";
import {gsap} from "gsap";
import Stats from "components/landing/Stats";
import PlatformUsers from "components/landing/PlatformUsers";
import WhyNexus from "components/landing/WhyNexus";

const AnimatedHeading = () => {
  const headingRef = useRef(null);

  useEffect(() => {
    const heading = headingRef.current;
    if (!heading) return;

    // Create GSAP context (auto-cleanup + scope)
    const ctx = gsap.context(() => {
      // Split text into words while preserving spaces and <br>
      const nodes = Array.from(heading.childNodes);
      const wordElements = [];

      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const fragments = node.textContent.split(/(\s+)/).filter(Boolean);

          fragments.forEach((fragment) => {
            if (/^\s+$/.test(fragment)) {
              // Preserve exact whitespace
              const space = document.createElement("span");
              space.textContent = fragment;
              node.parentNode.insertBefore(space, node);
              return;
            }

            // Actual word — wrap for animation
            const span = document.createElement("span");
            span.className = "word";
            span.textContent = fragment;
            span.style.opacity = "0";
            span.style.display = "inline-block";
            span.style.transform = "translateX(80px)";

            wordElements.push(span);
            node.parentNode.insertBefore(span, node);
          });

          node.parentNode.removeChild(node);
        }
        // <br> tags are preserved automatically — no action needed
      };

      nodes.forEach(processNode);

      // Animate only real words
      gsap.to(wordElements, {
        x: 0,
        opacity: 1,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.08,
        delay: 0.3,
        onComplete: () => {
          // Clean inline styles so layout is pristine
          wordElements.forEach((el) => {
            el.style.cssText = "";
          });
        },
      });
    }, heading); // ← scope to headingRef

    // Automatic cleanup on unmount (tweens + inline styles)
    return () => ctx.revert();
  }, []);

  return (
    <h2 ref={headingRef}>
      Your Trusted Insurance<br /> Partner
    </h2>
  );
};




const Home = () => {
  const date = new Date();
  const year = date.getFullYear();

  const paragraphRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const tl = gsap.timeline();

    tl.fromTo(
      paragraphRef.current,
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.2, ease: "power3.out" }
    ).fromTo(
      buttonRef.current,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.3, ease: "power3.out" },
      "-=0.25" 
    );

    return () => tl.kill(); 
  }, []);

  // The actual button
  const ActionButton = styled.button`
    background: transparent;
    color: white;
    font-size: 18px;
    font-weight: 600;
    padding: 12px 78px;
    border: 1px solid white;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 20px;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
    }
  `;


  // Minimal brand color palette
  const brandColors = {
    primary: "#1a1a1a",
    accent: "#3b82f6",
    secondary: "#f8fafc",
    white: "#ffffff",
    lightGrey: "#f5f5f5",
    mediumGrey: "#6b7280",
    darkGrey: "#374151",
    orangeRed: "#FF4500",
    blue: "#3b82f6",
  };


  // Styled component for the header section
  const HeaderSection = styled.header`
    position: relative;
    min-height: 100vh;
    color: ${brandColors.white};
    overflow-x: hidden;

    .navbar-container {
      position: absolute;
      top: 0;
      start: 0;
      width: 100%;
      z-index: 1000;
    }
  `;

  // Styled component for the hero section
  const HeroSection = styled.div`
    position: relative;
    min-height: 100vh;
    background: #003049;
    background-color: #003049;
    // opacity: 1;
    // background-image: radial-gradient(#444cf7 0.5px, #003049 0.5px);
    // background-size: 10px 10px;
    display: flex;
    align-items: center; /* vertical centering */
    // justify-content: center;
    // text-align: center;
    padding: 100px 20px;
    color: ${brandColors.white};
    overflow-x: hidden;

    .container {
      position: relative;
      z-index: 3;
      max-width: 1200px;
    }

    h2 {
      font-size: clamp(2.5rem, 6vw, 3.5rem);
      font-weight: 800;
      color: ${brandColors.white};
      line-height: 1.3;
      margin-bottom: 20px;
      padding: 20px 0;
    }

    p {
      font-size: 1.1rem;
      color: rgba(255, 255, 255, 0.9);
      max-width: 700px;
      margin: 0 0 30px;
      line-height: 1.6;
    }

    .btn {
      display: inline-block;
      padding: 12px 28px;
      background: ${brandColors.accent};
      color: ${brandColors.white};
      border-radius: 8px;
      font-weight: 600;
      text-decoration: none;
      transition: background 0.3s;

      &:hover {
        background: ${brandColors.blue};
      }
    }

    .video-container {
      margin-top: 70px;
      width: 100%;
      max-width: 1200px;
      height: 400px; /* Adjust height as needed */
      position: relative;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    }

    .background-video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      pointer-events: none; /* Prevents user interaction */
      opacity: 0.7; /* Slight opacity for better visibility */
    }
  `;

  // Styled component for the modules section
  const ModulesSection = styled.section`
    background-color: ${brandColors.secondary};
    padding: 120px 0;
    position: relative;

    .container {
      text-align: center;
    }

    .badge {
      background-color: ${brandColors.accent}15;
      color: ${brandColors.accent};
      border: 1px solid ${brandColors.accent}30;
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 0.95rem;
      font-weight: 500;
    }

    h2 {
      font-size: clamp(2.5rem, 5vw, 3.5rem);
      font-weight: 700;
      color: ${brandColors.primary};
      margin-bottom: 20px;
    }

    p {
      font-size: clamp(1.1rem, 2.5vw, 1.2rem);
      color: ${brandColors.mediumGrey};
      max-width: 600px;
      margin: 0 auto 30px;
    }

    .modules-container {
      display: grid;
      grid-template-columns: 1fr 1fr; /* Split into two equal columns */
      gap: 40px;
      align-items: center;

      @media (max-width: 992px) {
        grid-template-columns: 1fr; /* Stack vertically on smaller screens */
        gap: 20px;
      }
    }

    .image-container {
      width: 100%;
      height: 100%;
      max-height: 500px; /* Adjust as needed */
      overflow: hidden;
      border-radius: 8px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover; /* Ensure the image covers the container */
        display: block;
      }
    }

    .content-container {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .card {
      background-color: ${brandColors.accent}08;
      border: 2px solid ${brandColors.accent}20;
      border-radius: 8px;
      padding: 20px;
      text-align: center;
      transition: transform 0.3s, box-shadow 0.3s;

      &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
      }

      h3 {
        font-size: 1.1rem;
        color: ${brandColors.accent};
        font-weight: 600;
      }

      p {
        font-size: 0.9rem;
        color: ${brandColors.mediumGrey};
      }

      .icon-container {
        width: 60px;
        height: 60px;
        background-color: ${brandColors.white};
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        margin-bottom: 15px;
      }
    }
  `;

  // Styled component for the footer section
  const FooterSection = styled.footer`
    background: #003049;
    color: ${brandColors.white};
    padding: 40px 0;

    .container {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 20px;

      .column {
        flex: 1;
        min-width: 150px;

        h4 {
          font-weight: 600;
          margin-bottom: 15px;
        }

        p {
          color: rgba(255, 255, 255, 0.75);
          margin-bottom: 10px;
          font-size: 0.9rem;
        }

        a {
          display: block;
          color: rgba(255, 255, 255, 0.75);
          text-decoration: none;
          margin-bottom: 10px;
          transition: color 0.3s;

          &:hover {
            color: ${brandColors.accent};
          }
        }

        .social-link {
          width: 40px;
          height: 40px;
          background-color: #1DA1F2;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          margin-right: 10px;
          transition: transform 0.3s, box-shadow 0.3s;

          &:hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 20px rgba(29, 161, 242, 0.4);
          }

          i {
            color: ${brandColors.white};
            font-size: 16px;
          }
        }

        .contact-item {
          display: flex;
          align-items: center;
          margin-bottom: 10px;

          i {
            color: ${brandColors.orangeRed};
            margin-right: 10px;
            width: 16px;
          }

          span {
            color: rgba(255, 255, 255, 0.75);
          }
        }
      }

      .newsletter {
        h4 {
          font-weight: 600;
          margin-bottom: 15px;
        }

        p {
          color: rgba(255, 255, 255, 0.75);
          margin-bottom: 15px;
          font-size: 0.9rem;
        }

        .input-group {
          display: flex;
          border-radius: 8px;
          overflow: hidden;

          input {
            flex: 1;
            border: none;
            height: 40px;
            padding: 0 15px;
            background: ${brandColors.mediumGrey};
            color: ${brandColors.white};

            &::placeholder {
              color: rgba(255, 255, 255, 0.5);
            }
          }

          button {
            background-color: ${brandColors.blue};
            color: ${brandColors.white};
            border: none;
            height: 40px;
            padding: 0 15px;
            cursor: pointer;
            transition: background-color 0.3s;

            &:hover {
              background-color: ${brandColors.orangeRed};
            }
          }
        }
      }
    }

    .bottom-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 20px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      margin-top: 20px;

      p {
        margin: 0;
        color: rgba(255, 255, 255, 0.75);
        font-size: 0.8rem;
      }

      .social-icons {
        display: flex;
        gap: 10px;

        a {
          display: inline-flex;
          width: 30px;
          height: 30px;
          background-color: ${brandColors.mediumGrey};
          border-radius: 50%;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          transition: background-color 0.3s;

          &:hover {
            background-color: ${brandColors.accent};
          }

          i {
            color: ${brandColors.white};
            font-size: 14px;
          }
        }
      }
    }
  `;

  return (
    <div style={{ overflowX: "hidden" }}>
      {/* Header Section - Clean and Minimal */}
      <HeaderSection>
        <div className="navbar-container">
          <Navbar />
        </div>

        <HeroSection>
          <div className="container">
            <div className="row">
              <div className="col-12" style={{ paddingTop: '100px', paddingBottom: "50px", display:"flex" }}>
                <div>
              
                <AnimatedHeading />
              <p ref={paragraphRef}>
                  Comprehensive solutions to protect individuals from
                  life-threatening illnesses and accidents with professional-grade
                  tools and analytics. Comprehensive solutions to protect individuals from
                  life-threatening illnesses and accidents with professional-grade
                  tools and analytics.
                </p>

                <ActionButton ref={buttonRef}>Get early access</ActionButton>
             
                </div>
               <div style={{ display: "flex", justifyContent: "right", background: "#red", marginLeft: "auto" }}>
                 <GrokIcon />
               </div>

               
              </div>
              {/* <div className="col-12">
                <div className="video-container">
                  <video
                    className="background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                  >
                    <source src={video} type="video/mp4"  />

                    Your browser does not support the video tag.
                  </video>
                </div>
              </div> */}
            </div>
          </div>
        </HeroSection>
      </HeaderSection>

      <main style={{ overflowX: "hidden" }}>
        <PlatformUsers />
        <WhyNexus />
        <Features />
        <Services />
        <Stats />
      </main>

      <FooterSection id="footer">
        <div className="container">
          <div className="column">
            <h4 style={{ color: "#c1cbd2" }}>Nexus Intermediary Portal</h4>
            <p className="text-white-75">
              Premier insurance platform delivering professional-grade solutions
              for modern intermediaries and risk management professionals.
            </p>
          </div>
          <div className="column">
            <h4 style={{ color: "#c1cbd2" }}>Contact</h4>
            <div className="contact-item">
              <i className="fas fa-phone"></i>
              <span className="text-white-75">0723350348</span>
            </div>
            <div className="contact-item">
              <i className="fas fa-envelope"></i>
              <span className="text-white-75">info@nexus.ke</span>
            </div>
          </div>
          <div className="column newsletter">
            <h4 style={{ color: "#c1cbd2" }}>Newsletter</h4>
            <p>
              The latest news, articles, and resources, sent to your inbox weekly.
            </p>
            <div className="input-group">
              <input
                type="email"
                className="form-control"
                placeholder="Your email address..."
              />
              <button className="btn" type="button">
                Subscribe
              </button>
            </div>
          </div>
        </div>

        <div className="bottom-row container">
          <p>© Nexus Inc {year} | All Rights Reserved</p>
          <div className="social-icons">
            <a href="#" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
            <a href="#" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
            <a href="#" aria-label="Twitter"><i className="fab fa-twitter"></i></a>
          </div>
        </div>
      </FooterSection>
    </div>
  );
};

export default Home;